"use client"

import { useState, useEffect, useMemo } from "react"
import axios from "axios"
import * as XLSX from "xlsx"
import { API_URL } from "../../config"
import { useLanguage } from "../../context/LanguageContext"
import { useNotification } from "../../context/NotificationContext"
import "./WeatherDataManagement.css"

const PAGE_SIZE = 10 // Items per page for pagination

const WeatherDataManagement = () => {
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()
  const [weatherData, setWeatherData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [file, setFile] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [filter, setFilter] = useState({
    startDate: "",
    endDate: "",
    location: "",
  })

  // Pagination & Search & Sort states
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortConfig, setSortConfig] = useState({ key: "date", direction: "desc" })

  // Fetch weather data on mount or filter change
  useEffect(() => {
    fetchWeatherData()
  }, [])

  // Fetch weather data function
  const fetchWeatherData = async () => {
    try {
      setLoading(true)
      setError(null)

      let url = `${API_URL}/api/weather/adminHistory`

      // Add filters if they exist
      const params = new URLSearchParams()
      if (filter.startDate) params.append("startDate", filter.startDate)
      if (filter.endDate) params.append("endDate", filter.endDate)
      if (filter.location) params.append("location", filter.location)

      if (params.toString()) {
        url += `?${params.toString()}`
      }

      const token = localStorage.getItem("token")
      const res = await axios.get(url, {
        headers: {
          "x-auth-token": token,
        },
      })

      setWeatherData(res.data)
      setLoading(false)
      setCurrentPage(1) // Reset page on new fetch
    } catch (err) {
      console.error(err)
      setError("Failed to fetch weather data")
      setLoading(false)
    }
  }

  // Handle Excel file change
  const handleFileChange = (e) => {
    setFile(e.target.files[0])
  }

  const handleFilterChange = (e) => {
    setFilter({
      ...filter,
      [e.target.name]: e.target.value,
    })
  }

  const handleFilterSubmit = (e) => {
    e.preventDefault()
    fetchWeatherData()
  }

  // Handle Excel import
  const handleImport = async (e) => {
    e.preventDefault()
  
    if (!file) {
      showNotification(t("Please select a file to upload"), "error")
      return
    }
  
    try {
      setIsUploading(true)
      setUploadProgress(0)
  
      const formData = new FormData()
      formData.append("file", file) // key "file" should match your backend
  
      const token = localStorage.getItem("token")
  
      const res = await axios.post(
        `${API_URL}/api/weather/import`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-auth-token": token,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            setUploadProgress(percentCompleted)
          },
        }
      )
  
      showNotification(t(res.data.msg), "success")
      setFile(null)
      setIsUploading(false)
      setUploadProgress(0)
  
      fetchWeatherData()
    } catch (err) {
      console.error(err)
      setIsUploading(false)
      showNotification(
        t(err.response?.data?.msg || "Failed to import weather data"),
        "error"
      )
    }
  }
  

  const handleDelete = async (id) => {
    if (!window.confirm(t("Are you sure you want to delete this weather data?"))) {
      return
    }

    try {
      const token = localStorage.getItem("token")
      await axios.delete(`${API_URL}/api/weather/${id}`, {
        headers: {
          "x-auth-token": token,
        },
      })

      showNotification(t("Weather data deleted successfully"), "success")

      setWeatherData(weatherData.filter((data) => data._id !== id))
    } catch (err) {
      console.error(err)
      showNotification(
        t(err.response?.data?.msg || "Failed to delete weather data"),
        "error"
      )
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  // Search filter
  const filteredData = useMemo(() => {
    if (!searchTerm) return weatherData
    return weatherData.filter((data) => {
      const locationString = data.location?.coordinates
        ? `${data.location.coordinates[0]},${data.location.coordinates[1]}`
        : ""
      return (
        locationString.includes(searchTerm) ||
        (data.source && data.source.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (data._id && data._id.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    })
  }, [weatherData, searchTerm])

  // Sorting
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData

    const sorted = [...filteredData].sort((a, b) => {
      let aKey = a[sortConfig.key]
      let bKey = b[sortConfig.key]

      // Special handling for date and source fields
      if (sortConfig.key === "date") {
        aKey = new Date(aKey)
        bKey = new Date(bKey)
      } else if (sortConfig.key === "source") {
        aKey = aKey || ""
        bKey = bKey || ""
        aKey = aKey.toLowerCase()
        bKey = bKey.toLowerCase()
      }

      if (aKey < bKey) return sortConfig.direction === "asc" ? -1 : 1
      if (aKey > bKey) return sortConfig.direction === "asc" ? 1 : -1
      return 0
    })

    return sorted
  }, [filteredData, sortConfig])

  // Pagination slice
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE
    return sortedData.slice(startIndex, startIndex + PAGE_SIZE)
  }, [sortedData, currentPage])

  // Total pages count
  const totalPages = Math.ceil(sortedData.length / PAGE_SIZE)

  // Sorting toggle handler
  const requestSort = (key) => {
    let direction = "asc"
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc"
    }
    setSortConfig({ key, direction })
  }

  // Sample Excel file URL (You need to create and host this file yourself)
  const sampleFileUrl = "/sample-weather-data.xlsx" // Make sure you put sample-weather-data.xlsx in your public folder

  return (
    <div className="weather-data-management-container" dir={direction}>
      <div className="weather-data-management-header">
        <h1>{t("Weather Data Management")}</h1>
        <p className="subtitle">{t("Import, view, and manage weather data")}</p>
      </div>

      <div className="weather-data-management-content">
        <div className="import-section">
          <h2>{t("Import Weather Data")}</h2>
          <p>{t("Upload an Excel (.xlsx) file containing weather data")}</p>
          <p>
            {t("Download sample file:")}{" "}
            <a href={sampleFileUrl} download>
              {t("Sample Excel File")}
            </a>
          </p>

          <form onSubmit={handleImport} className="import-form">
            <div className="form-group">
              <label htmlFor="weatherData">{t("Weather Data File (Excel .xlsx)")}</label>
              <input
                type="file"
                id="weatherData"
                accept=".xlsx"
                onChange={handleFileChange}
                disabled={isUploading}
              />
            </div>

            {isUploading && (
              <div className="progress-container">
                <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                <span className="progress-text">{uploadProgress}%</span>
              </div>
            )}

            <button type="submit" className="import-button" disabled={!file || isUploading}>
              {isUploading ? t("Importing...") : t("Import Data")}
            </button>
          </form>
        </div>

        <div className="filter-section">
          <h2>{t("Filter Weather Data")}</h2>

          <form onSubmit={handleFilterSubmit} className="filter-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">{t("Start Date")}</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={filter.startDate}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="endDate">{t("End Date")}</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={filter.endDate}
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="location">{t("Location (lon,lat)")}</label>
              <input
                type="text"
                id="location"
                name="location"
                value={filter.location}
                onChange={handleFilterChange}
                placeholder="e.g. 45.123,32.456"
              />
            </div>

            <button type="submit" className="filter-button">
              {t("Apply Filters")}
            </button>
          </form>
        </div>

        <div className="data-section">
          <h2>{t("Weather Data List")}</h2>

          <div className="search-container" style={{ marginBottom: "1rem" }}>
            <input
              type="text"
              placeholder={t("Search by location, source, or ID...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>{t("Loading weather data...")}</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i> {t(error)}
            </div>
          ) : sortedData.length === 0 ? (
            <p className="no-data">{t("No weather data found")}</p>
          ) : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th
                        onClick={() => requestSort("date")}
                        style={{ cursor: "pointer" }}
                        title={t("Sort by Date")}
                      >
                        {t("Date")} {sortConfig.key === "date" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th>{t("Location")}</th>
                      <th>{t("Temperature")}</th>
                      <th>{t("Humidity")}</th>
                      <th>{t("Wind Speed")}</th>
                      <th>{t("Precipitation")}</th>
                      <th
                        onClick={() => requestSort("source")}
                        style={{ cursor: "pointer" }}
                        title={t("Sort by Source")}
                      >
                        {t("Source")} {sortConfig.key === "source" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th>{t("Actions")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.map((data) => (
                      <tr key={data._id}>
                        <td>{formatDate(data.date)}</td>
                        <td>
                          {data.locationName}
                        </td>
                        <td>{data.temperature ?? t("N/A")}</td>
                        <td>{data.humidity ?? t("N/A")}</td>
                        <td>{data.windSpeed ?? t("N/A")}</td>
                        <td>{data.precipitation ?? t("N/A")}</td>
                        <td>{data.source ?? t("N/A")}</td>
                        <td>
                          <button
                            className="delete-button"
                            onClick={() => handleDelete(data._id)}
                            title={t("Delete")}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="pagination-container">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  {t("Prev")}
                </button>
                <span>
                  {t("Page")} {currentPage} {t("of")} {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  {t("Next")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default WeatherDataManagement
